import { User } from "../../types/common/user";
import { IFormField, ISuggestionForm, FieldDestination } from "../../types/suggestions/form";
import { DocumentBody } from "../../services/documents/Documents";
import { IListItemReq, IUpdateUPReq } from "../../services/sharepoint/Sharepoint";

interface ProcessedField {
  key: string;    // Final property name (e.g., "PBSRegion1" from "PBSRegion1Id")
  value: any;     // Processed value (dates converted, placeholders resolved)
}

/**
 * Extracts the stored procedure name from a proc call pattern
 * @param proc - Stored procedure pattern (e.g., "pInsertNewStudentSupervisorRelationship @Student = '{UserAccount}', @Supervisor = '{CurrentValue0}'")
 * @returns The procedure name (e.g., "pInsertNewStudentSupervisorRelationship")
 */
export function getProcName(proc: string): string {
  // Match the procedure name - everything before the first space or @ symbol
  const match = proc.match(/^(\w+)/);
  return match ? match[1] : '';
}

/**
 * Parses a stored procedure call pattern and returns parameter mappings
 * @param proc - Stored procedure pattern (e.g., "pInsertNewStudentSupervisorRelationship @Student = '{UserAccount}', @Supervisor = '{CurrentValue0}'")
 * @param user - Current user object for context replacement
 * @param currentValue - Comma-separated string of current values to be accessed via {CurrentValue0}, {CurrentValue1}, etc.
 * @returns Record<string, any> with parameter names as keys and resolved values
 */
export function getProcParams(proc: string, user: User, currentValue: string): Record<string, any> {
  const params: Record<string, any> = {};
  // Split currentValue by comma to create an array of values
  const currentValues = currentValue ? currentValue.split('|').map(v => v.trim()) : [];

  // Regular expression to match @ParameterName = '{PlaceholderValue}', @ParameterName = {PlaceholderValue}, or @ParameterName = 'literalValue'
  const paramRegex = /@(\w+)\s*=\s*'?(\{[^}]+\}|[^',\s]+)'?/g;

  let match;
  while ((match = paramRegex.exec(proc)) !== null) {
    const paramName = match[1]; // Parameter name (e.g., "Student")
    let paramValue = match[2].trim(); // Placeholder value (e.g., "{UserAccount}" or "{CurrentValue0}")
    // Replace user context placeholders
    paramValue = prepopulateValue(paramValue, user, currentValues);

    params[paramName] = paramValue;
  }

  return params;
}

/**
 * Prepopulates placeholder values with actual user context and current values
 * @param value - The value that may contain placeholders
 * @param user - Current user object
 * @param currentValues - Array of current values
 * @returns The value with placeholders replaced
 */
function prepopulateValue(value: any, user: User | undefined, currentValues: string[]): any {
  if (!user) return value;
  if (typeof value === 'string') {
    return value
      .replace(/\{UserID\}/g, user.SPID.toString())
      .replace(/\{UserRegionID\}/g, user.RegionID.toString())
      .replace(/\{UserEmail\}/g, user.Email)
      .replace(/\{UserAccount\}/g, user.LoginName.replace('positive\\', ''))
      .replace(/\{UserRegion\}/g, user.Region || '')
      .replace(/\{UserRole\}/g, user.Role || '')
      .replace(/\{UserFullName\}/g, user.FullName || '')
      .replace(/\{CurrentValue(\d+)\}/g, (match, index) => {
        const idx = parseInt(index, 10);
        return currentValues[idx] !== undefined ? currentValues[idx] : match;
      })
      .replace(/\{CurrentValue\}/g, () => {
        // Treat {CurrentValue} without index as {CurrentValue0}
        return currentValues[0] !== undefined ? currentValues[0] : '{CurrentValue}';
      });
  } else if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) {
      return value.map((item: any) => prepopulateValue(item, user, currentValues));
    }
    const prepopulated: any = {};
    for (const key in value) {
      prepopulated[key] = prepopulateValue(value[key], user, currentValues);
    }
    return prepopulated;
  }

  return value;
}

/**
 * Process a single field value based on field definition
 * @param field - The field definition
 * @param rawValue - The raw value from formState
 * @param currentUser - Current user object
 * @param currentValues - Array of current values for placeholder replacement
 * @returns ProcessedField with final key and value
 */
export function processFieldValue(
  field: IFormField,
  rawValue: any,
  currentUser: User,
  currentValues: string[]
): ProcessedField {
  let key = field.id;
  let value = rawValue;
  // Handle PBSRegion1Id special case
  if (field.id === 'PBSRegion1Id') {
    key = 'PBSRegion1';
    value = currentUser.RegionID
  } else {
    // Remove "Id" suffix from field names
    if (field.id.endsWith('Id') && typeof value !== 'object') {
      key = field.id.slice(0, -2);
    }
    
    // Handle Internal fields with predefined values
    if (field.type === 'Internal' && field.value !== undefined) {
      value = prepopulateValue(field.value, currentUser, currentValues);
    }

    //Handle PBSRegionId if it's structured as {results: ['166']}
    if (field.id === 'PBSRegionId' && typeof value === 'object') {
      value = resultsJsonToBracketedString(value);
    }
    // Date field conversion
    if (field.type === 'Date' && value) {
      if (value instanceof Date) {
        value = value.toISOString();
      } else if (typeof value === 'string') {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          value = date.toISOString();
        }
      }
    }

    // Digit-only text fields
    if (field.type === 'Text' && (field as any).digitonly && typeof value === 'string') {
      value = value.replace(/\D/g, '');
    }
    if (field.type === 'Text' && field.prefix) {
      value = field.prefix + value;
    }

    // Stringify object values so they can be sent as strings to SharePoint/APIs
    if (typeof value === 'object' && value !== null) {
      value = JSON.stringify(value);
    }
  }

  return { key, value };
}

/**
 * Group fields by their destinations
 * @param fields - Array of form fields
 * @param formState - Current form state with user-entered values
 * @param currentUser - Current user object
 * @param currentValues - Array of current values for placeholder replacement
 * @returns Map of destination name to field properties object
 */
export function groupFieldsByDestination(
  fields: IFormField[],
  formState: Record<string, any>,
  currentUser: User,
  currentValues: string[]
): Map<FieldDestination, Record<string, any>> {
  const grouped = new Map<FieldDestination, Record<string, any>>();

  fields.forEach(field => {
    // Skip fields without destinations
    if (!field.destinations || field.destinations.length === 0) {
      return;
    }

    // Get raw value from form state (for Internal fields, use field.value)
    const rawValue = field.type === 'Internal' ? field.value : formState[field.id];
    
    // Skip if no value and field is not Internal type
    if (rawValue === undefined && field.type !== 'Internal') {
      return;
    }

    const processed = processFieldValue(field, rawValue, currentUser, currentValues);

    // Add processed field to each of its destinations
    // Each destination may require a different key for the same field
    field.destinations.forEach(dest => {
      if (!grouped.has(dest)) {
        grouped.set(dest, {});
      }
      const destFields = grouped.get(dest)!;
      
      // Determine the key based on destination:
      // - UserProfile: use field id (processed.key)
      // - ListItem: use listcolumnid if available, otherwise field id
      let finalKey: string;
      if (dest === 'UserProfile') {
        finalKey = field.id;
      } else if ((dest === 'ListItem' || dest === 'DocumentLibrary') && (field as any).listcolumnid) {
        finalKey = (field as any).listcolumnid;
      } else {
        finalKey = processed.key;
      }
      
      destFields[finalKey] = processed.value;
    });
  });

  return grouped;
}

/**
 * Infer destinations from form type for backward compatibility
 * @param form - The suggestion form
 */
export function inferDestinationsFromFormType(form: ISuggestionForm): void {
  const typeToDestinations: Record<string, FieldDestination[]> = {
    'UploadDocuments': ['DocumentLibrary'],
    'UploadDocumentsAndUpdateExternal': ['DocumentLibrary', 'External'],
    'UpdateUserProfile': ['UserProfile'],
    'UpdateUserProfileAndUploadDocs': ['UserProfile', 'DocumentLibrary'],
    'UpdateListItem': ['ListItem'],
    'UpdateUserProfileAndListItem': ['UserProfile', 'ListItem'],
    'AddListItem': ['ListItem'],
  };

  const defaultDestinations = typeToDestinations[form.type] || [];
  
  // If fields don't have destinations, apply default based on form type
  form.fields.forEach(field => {
    if (!field.destinations || field.destinations.length === 0) {
      field.destinations = [...defaultDestinations];
    }
  });
}

/**
 * Build document upload request
 * @param file - The file to upload
 * @param fields - The processed fields for document metadata
 * @param form - The form configuration
 * @returns DocumentBody for the upload request
 */
export function buildDocumentUploadRequest(
  file: File,
  fields: Record<string, any>,
  form: ISuggestionForm
): DocumentBody {
  return {
    file: file,
    siteUrl: form.site,
    libraryName: form.library,
    overwrite: true,
    conflictBehavior: 'rename',
    metadata: {
      ...fields,
      Title: file.name // Always override Title with filename
    }
  };
}

/**
 * Build user profile update request
 * @param fields - The processed fields for user profile
 * @param currentUser - Current user object
 * @returns IUpdateUPReq for the update request
 */
export function buildUserProfileUpdateRequest(
  fields: Record<string, any>,
  currentUser: User
): IUpdateUPReq {
  // Convert dates to YYYY-MM-DD format for user profile
  const profileFields: Record<string, any> = {};
  
  Object.keys(fields).forEach(key => {
    let value = fields[key];
    
    // If it's an ISO date string, convert to YYYY-MM-DD for user profile
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
      value = value.split('T')[0];
    }
    
    profileFields[key] = value;
  });
  
  return {
    properties: profileFields,
    siteUrl: 'https://sharepoint.teampbs.com/my'
  };
}

/**
 * Build list item update request
 * @param fields - The processed fields for list item
 * @param form - The form configuration
 * @param currentUser - Current user object
 * @param currentValue - Current value string for dynamic placeholder resolution
 * @returns IListItemReq for the update request
 */
export function buildListItemUpdateRequest(
  fields: Record<string, any>,
  form: ISuggestionForm,
  currentUser: User,
  currentValue: string
): IListItemReq {
  const resolvedItemId = getListItemIdFromForm(form, currentUser, currentValue);
  
  // Convert dates to YYYY-MM-DD format for list items
  const listFields: Record<string, any> = {};
  
  Object.keys(fields).forEach(key => {
    let value = fields[key];
    
    // If it's an ISO date string, convert to YYYY-MM-DD for list items
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
      value = value.split('T')[0];
    }
    
    listFields[key] = value;
  });
  
  return {
    siteUrl: form.site,
    listName: form.list || '',
    itemId: resolvedItemId,
    fieldValues: listFields,
    user: 'POSITIVE\\' + currentUser.LoginName
  };
}

/**
 * gets listitemid from the form.
 * @param form - The form object that may contain listitemid as a number or placeholder string
 * @param user - Current user object
 * @param currentValues - Array of current values
 * @returns The value as a number
 */
export function getListItemIdFromForm(form: any, user: User, currentValues: string): number {
  const listitemid = form.listitemid;
  
  // Split currentValues by pipe to create an array
  const currentValuesArray = currentValues ? currentValues.split('|').map(v => v.trim()) : [];
  
  // If it's already a number, return it
  if (typeof listitemid === 'number') {
    return listitemid;
  }
  
  // If it's a string, try to parse as number first
  if (typeof listitemid === 'string') {
    // If it's a plain number string, parse it
    const parsed = parseInt(listitemid, 10);
    if (!isNaN(parsed) && listitemid === parsed.toString()) {
      return parsed;
    }
    
    // Otherwise, treat it as a dynamic value and prepopulate
    const resolved = prepopulateValue(listitemid, user, currentValuesArray);
    const resolvedParsed = parseInt(resolved, 10);
    
    if (!isNaN(resolvedParsed)) {
      return resolvedParsed;
    }
  }
  
  // Default to 0 if unable to resolve
  return 0;
}

/**
 * gets the field values from the form.
 * @param formFields - The form fields array containing field definitions
 * @param user - Current user object
 * @param currentValues - Pipe-delimited string of current values
 * @returns Record<string, any> of field values
 */
export function getFieldValuesFromForm(formFields: IFormField[], user: User, currentValues: string): Record<string, any> {
  const fieldValues: Record<string, any> = {};
  
  // Split currentValues by pipe to create an array
  const currentValuesArray = currentValues ? currentValues.split('|').map(v => v.trim()) : [];
  
  formFields.forEach((field: IFormField) => {
    const fieldId = field.id;
    
    // Only process Internal fields that have predefined values
    if (field.type === 'Internal' && field.value !== undefined && field.value !== null) {
      // Prepopulate the value with user context and current values
      fieldValues[fieldId] = prepopulateValue(field.value, user, currentValuesArray);
    }
  });
  
  return fieldValues;
}

/**
 * Converts a results JSON string like '{"results":["166"]}' into a bracketed string like '[166]'
 * @param value - The JSON string to convert
 * @returns A bracketed string of the numeric results, or the original value if parsing fails
 */
export function resultsJsonToBracketedString(value: {results: string[]}): string {
  try {
    let results = value.results;
    if (results && Array.isArray(results)) {
      let returnVal : string= '[' + results.map((r: string) => Number(r)).join(', ') + ']';
      return returnVal;
    }
  } catch {
    // fall through
  }
  return '';
}

//FOLLOWING FUNCTIONS ARE LEGACY FROM PREVIOUS FORM IMPLEMENTATION AND MAY BE DEPRECATED SOON
export function getMetadata(formFields: any, formData: any, isMultiWithList?: boolean) {

  let metadata: any = {};
  formFields.forEach((field: any) => {

    let column = field.id;

    if (typeof isMultiWithList !== 'undefined' && isMultiWithList === true) {
      column = field.listcolumnid;
    }

    if (field.type === "Internal") {
      metadata[column] = field.value; //directly set the value

      //copy the value from previously set field
      //field.value will contain the id of the field from which to copy from
      if (field.copy === true && formData[field.value]) {
        metadata[column] = formData[field.value];
      }

    }
    else if (field.type === "Text") {
      if (formData[field.id]) {
        let _fieldValue = formData[field.id];
        if (field.digitonly) { _fieldValue = _fieldValue.replace(/\D/g, ''); }
        if (field.prefix) { _fieldValue = field.prefix + _fieldValue }
        metadata[column] = _fieldValue;
      }
    }
    else if (field.type === "Note") {
      if (formData[field.id]) {
        let _fieldValue = formData[field.id];
        if (field.prefix) { _fieldValue = field.prefix + _fieldValue }
        metadata[column] = _fieldValue;
      }
    }
    else if (field.type === "Date") {
      if (formData[field.id]) {
        metadata[column] = new Date(formData[field.id]).toISOString();
      }
    }
    else if (field.type === "Person") {
      if (formData[field.id]) {
        metadata[column] = formData[field.id];
      }
    }
    else if (field.type === "Boolean") {
      if (formData[field.id]) {
        metadata[column] = formData[field.id];
      } else { //else if it is null then use default checked value
        metadata[column] = field.defaultChecked;
      }
    }
    else if (field.type === "List") {
      if (formData[field.id]) {
        if (field.multi) {

        } else {
          //single value case
          metadata[column] = formData[field.id];
        }
      } else { //if nothing is selected and a default value exists

        if (field.default) {

          metadata[column] = field.default;

        }

      }

    }
  });

  return metadata;
}

export function replaceContextValues(value: string, props: any) {

  return value.replace(new RegExp("\"{UserID}\"", 'g'), props.userID) //numeric value
    .replace(new RegExp("\"{UserRegionID}\"", 'g'), props.userRegionID) //numeric value
    .replace(new RegExp("{UserAccount}", 'g'), props.userAccount.replace("positive\\", ""))
    .replace(new RegExp("{UserEmail}", 'g'), props.userEmail)
    .replace(new RegExp("{UserRegion}", 'g'), props.userRegion)
    .replace(new RegExp("{UserRole}", 'g'), props.userRole);
}

export function _copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
  const key = columnKey as keyof T;
  return items.slice(0).sort((a: T, b: T) => ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1));
}

export function getSimpleDateFormat(sDate: string) {

  var d = sDate.slice(0, 10).split('-');
  return d[1] + '/' + d[2] + '/' + d[0];

}

export function formatMonthDate(date: any) {
  if (date !== undefined && date !== "") {
    var myDate = new Date(date);
    var month = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ][myDate.getMonth()];
    var str = myDate.getDate() + " " + month;
    return str;
  }
  return "";
}

export function dynamicSort(property: any) {
  var sortOrder = 1;
  if (property[0] === "-") {
    sortOrder = -1;
    property = property.substr(1);
  }
  return function (a: any, b: any) {
    /* next line works with strings and numbers,
     * and you may want to customize it to your needs
     */
    var result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
    return result * sortOrder;
  }
}

export function addDays(date: any, days: any) {
  var result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function numberWithCommas(x: any) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function addTimeStampToFileName(fileName: string, userName: string) {
  let currentTimeStamp = new Date();
  let fileParts = fileName.split(".");
  return fileParts[0] + "_" + userName + "_" + currentTimeStamp.toISOString().replace(/\./g, '').replace(/:/g, '') + "." + fileParts[fileParts.length - 1];
}
